import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, Chip, Divider, Tabs, Tab,
  InputAdornment,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import { nombreTipoDocumento } from '../utils/whatsapp';

const TIPOS_DOC = [
  { value: null,   label: 'Por defecto (todos los documentos)' },
  { value: '01',   label: 'Factura Electrónica (01)' },
  { value: '03',   label: 'Boleta de Venta (03)' },
  { value: '07',   label: 'Nota de Crédito (07)' },
  { value: '08',   label: 'Nota de Débito (08)' },
  { value: '09',   label: 'Guía de Remisión Remitente (09)' },
  { value: '31',   label: 'Guía de Remisión Transportista (31)' },
  { value: 'CTZ',  label: 'Cotización' },
];

const VARIABLES = [
  { var: '{{nombre_empresa}}',       label: 'Empresa' },
  { var: '{{nombre_cliente}}',       label: 'Cliente' },
  { var: '{{tipo_documento_nombre}}', label: 'Tipo doc.' },
  { var: '{{serie}}',                label: 'Serie' },
  { var: '{{numero}}',               label: 'Número' },
  { var: '{{fecha}}',                label: 'Fecha' },
  { var: '{{total}}',                label: 'Total' },
  { var: '{{moneda}}',               label: 'Moneda' },
  { var: '{{link_pdf}}',             label: 'Link PDF' },
];

const EMPTY_FORM = {
  tipo_documento: null,
  nombre: 'Plantilla por defecto',
  asunto_template: '',
  cuerpo_html_template: '',
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function ConfiguracionEmail() {
  const [tab, setTab] = useState(0);

  // ─── SMTP ────────────────────────────────────────────────────────────────────
  const [smtp, setSmtp] = useState({ email_envio: '', smtp_servidor: '', smtp_puerto: '', smtp_usuario: '', smtp_clave: '', smtp_ssl: 0 });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpMsg, setSmtpMsg] = useState({ type: '', text: '' });
  const [showClave, setShowClave] = useState(false);
  const [emailPrueba, setEmailPrueba] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    setSmtpLoading(true);
    axios.get('/api/email/smtp')
      .then(r => setSmtp({ ...smtp, ...r.data }))
      .catch(() => {})
      .finally(() => setSmtpLoading(false));
  }, []);

  const handleSmtpSave = async () => {
    setSmtpSaving(true);
    setSmtpMsg({ type: '', text: '' });
    try {
      await axios.post('/api/email/smtp', smtp);
      setSmtpMsg({ type: 'success', text: 'Configuración SMTP guardada correctamente.' });
    } catch (e) {
      setSmtpMsg({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleSmtpTest = async () => {
    setSmtpTesting(true);
    setSmtpMsg({ type: '', text: '' });
    try {
      const r = await axios.post('/api/email/smtp/test');
      setSmtpMsg({ type: 'success', text: `Conexión verificada: ${r.data.host}:${r.data.puerto}` });
    } catch (e) {
      setSmtpMsg({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!emailPrueba.trim()) return;
    setSendingTest(true);
    setSmtpMsg({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('to', emailPrueba.trim());
      formData.append('subject', 'Prueba de correo - InsightSoft');
      formData.append('html', '<p>Este es un correo de prueba enviado desde InsightSoft ERP.</p><p>Si lo recibes, la configuración SMTP está funcionando correctamente.</p>');
      formData.append('adjPdf', 'false');
      await axios.post('/api/email/enviar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSmtpMsg({ type: 'success', text: `Correo de prueba enviado a ${emailPrueba}` });
    } catch (e) {
      setSmtpMsg({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setSendingTest(false);
    }
  };

  // ─── PLANTILLAS ───────────────────────────────────────────────────────────────
  const [plantillas, setPlantillas] = useState([]);
  const [plantLoading, setPlantLoading] = useState(false);
  const [defaults, setDefaults] = useState({ asuntos: {}, cuerpos: {} });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [previewTab, setPreviewTab] = useState(0);
  const asuntoRef = useRef(null);
  const cuerpoRef = useRef(null);

  const loadPlantillas = useCallback(async () => {
    setPlantLoading(true);
    try {
      const [rp, rd] = await Promise.all([
        axios.get('/api/email/plantillas'),
        axios.get('/api/email/defaults'),
      ]);
      setPlantillas(rp.data || []);
      setDefaults(rd.data || { asuntos: {}, cuerpos: {} });
    } catch (_) {}
    setPlantLoading(false);
  }, []);

  useEffect(() => { loadPlantillas(); }, [loadPlantillas]);

  const openNew = () => {
    setEditForm({ ...EMPTY_FORM, asunto_template: defaults.asuntos?.DEFAULT || '', cuerpo_html_template: defaults.cuerpos?.DEFAULT || '' });
    setPreviewTab(0);
    setSaveMsg('');
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditForm({ ...p });
    setPreviewTab(0);
    setSaveMsg('');
    setDialogOpen(true);
  };

  const handleTipoChange = (val) => {
    const tipo = val === '' ? null : val;
    const asunto = defaults.asuntos?.[tipo] || defaults.asuntos?.DEFAULT || '';
    const cuerpo = defaults.cuerpos?.[tipo] || defaults.cuerpos?.DEFAULT || '';
    setEditForm(f => ({ ...f, tipo_documento: tipo, asunto_template: f.asunto_template || asunto, cuerpo_html_template: f.cuerpo_html_template || cuerpo }));
  };

  const handleRestoreDefault = () => {
    const tipo = editForm.tipo_documento;
    setEditForm(f => ({
      ...f,
      asunto_template: defaults.asuntos?.[tipo] || defaults.asuntos?.DEFAULT || '',
      cuerpo_html_template: defaults.cuerpos?.[tipo] || defaults.cuerpos?.DEFAULT || '',
    }));
  };

  const insertVar = (varStr, field) => {
    const ref = field === 'asunto' ? asuntoRef : cuerpoRef;
    const key = field === 'asunto' ? 'asunto_template' : 'cuerpo_html_template';
    const el = ref.current;
    if (!el) {
      setEditForm(f => ({ ...f, [key]: f[key] + varStr }));
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const current = editForm[key];
    const nuevo = current.substring(0, start) + varStr + current.substring(end);
    setEditForm(f => ({ ...f, [key]: nuevo }));
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + varStr.length;
      el.focus();
    }, 0);
  };

  const handleSave = async () => {
    if (!editForm.asunto_template.trim() || !editForm.cuerpo_html_template.trim()) {
      setSaveMsg('El asunto y el cuerpo son obligatorios.');
      return;
    }
    setSaving(true);
    setSaveMsg('');
    try {
      await axios.post('/api/email/plantillas', editForm);
      setDialogOpen(false);
      await loadPlantillas();
    } catch (e) {
      setSaveMsg(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar plantilla?')) return;
    try {
      await axios.delete(`/api/email/plantillas/${id}`);
      await loadPlantillas();
    } catch (_) {}
  };

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <EmailIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h5" fontWeight="bold">Configuración de Correo Electrónico</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Servidor SMTP" />
        <Tab label="Plantillas de Mensaje" />
      </Tabs>

      {/* ── TAB SMTP ── */}
      <TabPanel value={tab} index={0}>
        <Paper sx={{ p: 3, maxWidth: 560 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Configuración del servidor de correo saliente
          </Typography>
          {smtpLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                size="small" label="Correo de envío (remitente)"
                value={smtp.email_envio} onChange={e => setSmtp(s => ({ ...s, email_envio: e.target.value }))}
                placeholder="ventas@miempresa.com"
                helperText="Se mostrará como remitente del correo"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small" label="Servidor SMTP" sx={{ flex: 2 }}
                  value={smtp.smtp_servidor} onChange={e => setSmtp(s => ({ ...s, smtp_servidor: e.target.value }))}
                  placeholder="smtp.gmail.com"
                />
                <TextField
                  size="small" label="Puerto" sx={{ width: '8em' }}
                  value={smtp.smtp_puerto} onChange={e => setSmtp(s => ({ ...s, smtp_puerto: e.target.value }))}
                  placeholder="587"
                />
              </Box>
              <TextField
                size="small" label="Usuario SMTP"
                value={smtp.smtp_usuario} onChange={e => setSmtp(s => ({ ...s, smtp_usuario: e.target.value }))}
                placeholder="correo@gmail.com"
              />
              <TextField
                size="small" label="Contraseña / App Password"
                type={showClave ? 'text' : 'password'}
                value={smtp.smtp_clave} onChange={e => setSmtp(s => ({ ...s, smtp_clave: e.target.value }))}
                placeholder="Dejar en blanco para no cambiar"
                helperText="Para Gmail: usar Contraseña de aplicación (no la clave de cuenta)"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowClave(v => !v)}>
                        {showClave ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {smtpMsg.text && (
                <Alert severity={smtpMsg.type || 'info'} onClose={() => setSmtpMsg({ type: '', text: '' })}>
                  {smtpMsg.text}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleSmtpSave} disabled={smtpSaving}>
                  {smtpSaving ? <CircularProgress size={18} color="inherit" /> : 'Guardar'}
                </Button>
                <Button variant="outlined" onClick={handleSmtpTest} disabled={smtpTesting}>
                  {smtpTesting ? <CircularProgress size={18} color="inherit" /> : 'Verificar conexión'}
                </Button>
              </Box>
              <Divider />
              <Typography variant="caption" color="text.secondary" fontWeight="bold">ENVIAR CORREO DE PRUEBA</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small" label="Correo destino" sx={{ flex: 1 }}
                  value={emailPrueba} onChange={e => setEmailPrueba(e.target.value)}
                  placeholder="test@ejemplo.com"
                />
                <Button
                  variant="outlined" startIcon={<SendIcon />}
                  onClick={handleSendTestEmail} disabled={sendingTest || !emailPrueba.trim()}
                >
                  {sendingTest ? <CircularProgress size={18} color="inherit" /> : 'Enviar prueba'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* ── TAB PLANTILLAS ── */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Plantillas de correo por tipo de documento</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} size="small">Nueva plantilla</Button>
        </Box>
        {plantLoading ? <CircularProgress size={24} /> : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Tipo Documento</strong></TableCell>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell><strong>Asunto</strong></TableCell>
                  <TableCell align="right"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plantillas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No hay plantillas guardadas. Se usan los textos por defecto.
                    </TableCell>
                  </TableRow>
                )}
                {plantillas.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      {p.tipo_documento
                        ? <Chip label={p.tipo_documento} size="small" color="primary" variant="outlined" />
                        : <Chip label="Por defecto" size="small" />}
                    </TableCell>
                    <TableCell>{p.nombre}</TableCell>
                    <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem', color: 'text.secondary' }}>
                      {p.asunto_template}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleDelete(p.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* ── DIALOG EDITAR PLANTILLA ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight="bold">{editForm.id ? 'Editar plantilla de correo' : 'Nueva plantilla de correo'}</Typography>
          <Tooltip title="Restaurar texto por defecto">
            <IconButton size="small" onClick={handleRestoreDefault}><RestoreIcon fontSize="small" /></IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select size="small" label="Tipo de documento" sx={{ flex: 1 }}
                value={editForm.tipo_documento === null ? '' : editForm.tipo_documento}
                onChange={e => handleTipoChange(e.target.value)}
                SelectProps={{ native: true }}
              >
                {TIPOS_DOC.map(t => (
                  <option key={String(t.value)} value={t.value === null ? '' : t.value}>{t.label}</option>
                ))}
              </TextField>
              <TextField
                size="small" label="Nombre de la plantilla" sx={{ flex: 1 }}
                value={editForm.nombre}
                onChange={e => setEditForm(f => ({ ...f, nombre: e.target.value }))}
              />
            </Box>

            {/* Variables */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mr: 0.5 }}>
                VARIABLES (clic para insertar en asunto o cuerpo):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {VARIABLES.map(v => (
                  <Box key={v.var} sx={{ display: 'flex', gap: 0.25 }}>
                    <Chip label={v.label} size="small" onClick={() => insertVar(v.var, 'asunto')}
                      title={`Insertar ${v.var} en asunto`} sx={{ cursor: 'pointer', fontSize: '0.7rem' }} />
                    <Chip label="A" size="small" variant="outlined" onClick={() => insertVar(v.var, 'asunto')}
                      sx={{ cursor: 'pointer', fontSize: '0.6rem', minWidth: 20, display: 'none' }} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {VARIABLES.map(v => (
                  <Tooltip key={v.var} title={`Insertar en cuerpo: ${v.var}`}>
                    <Chip
                      label={`{${v.label}}`}
                      size="small"
                      variant="outlined"
                      onClick={() => insertVar(v.var, 'cuerpo')}
                      sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                Fila superior → inserta en Asunto &nbsp;|&nbsp; Fila inferior → inserta en Cuerpo
              </Typography>
            </Box>

            <TextField
              inputRef={asuntoRef}
              size="small" label="Asunto del correo"
              value={editForm.asunto_template}
              onChange={e => setEditForm(f => ({ ...f, asunto_template: e.target.value }))}
              fullWidth
              helperText="Puede usar variables {{nombre_empresa}}, {{serie}}, etc."
            />

            <Tabs value={previewTab} onChange={(_, v) => setPreviewTab(v)} sx={{ minHeight: 32 }}>
              <Tab label="Editar HTML" sx={{ minHeight: 32, py: 0, fontSize: '0.8rem' }} />
              <Tab label="Vista previa" sx={{ minHeight: 32, py: 0, fontSize: '0.8rem' }} />
            </Tabs>
            {previewTab === 0 ? (
              <TextField
                inputRef={cuerpoRef}
                multiline rows={10} size="small" fullWidth
                label="Cuerpo del correo (HTML)"
                value={editForm.cuerpo_html_template}
                onChange={e => setEditForm(f => ({ ...f, cuerpo_html_template: e.target.value }))}
                inputProps={{ style: { fontSize: '0.78rem', fontFamily: 'monospace' } }}
                helperText="Use etiquetas HTML: <p>, <strong>, <a href='...'>, <br>, etc."
              />
            ) : (
              <Box sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2,
                minHeight: 160, maxHeight: 280, overflowY: 'auto', fontSize: '0.88rem',
              }}
                dangerouslySetInnerHTML={{ __html: editForm.cuerpo_html_template }}
              />
            )}

            {saveMsg && <Alert severity="error">{saveMsg}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}>
            Guardar plantilla
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
